import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { ArrowRight, MapPin, Receipt, ChartBar, Truck, Sparkle, Check, Recycle, Drop, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import FranchiseSection from "@/components/FranchiseSection";
import usePageTitle from "@/lib/usePageTitle";

export default function Landing() {
  usePageTitle("Mülltonnenreinigung als Service");
  const { user } = useAuth();
  const navigate = useNavigate();

  const goLogin = () => {
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[var(--tonneva-bg)] text-[var(--tonneva-fg)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="tonneva-logo"><Logo size={36} textClass="text-xl" /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[var(--tonneva-muted)]">
            <a href="#features" className="hover:text-[var(--tonneva-primary)] transition">Features</a>
            <a href="#how" className="hover:text-[var(--tonneva-primary)] transition">Workflow</a>
            <a href="#pricing" className="hover:text-[var(--tonneva-primary)] transition">Preise</a>
            <a href="#kontakt" className="hover:text-[var(--tonneva-primary)] transition">Kontakt</a>
            <a href="#franchise" className="hover:text-[var(--tonneva-primary)] transition" style={{ color: "#3FA64E" }}>Franchise</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="tel:+4976215550100" className="hidden lg:flex items-center gap-2 text-sm font-semibold text-[var(--tonneva-primary-dark)] hover:opacity-80" data-testid="nav-phone">
              <Phone size={16} weight="bold" /> +49 7621 555 0100
            </a>
            {user ? (
              <Button onClick={() => navigate("/me")} className="hover:opacity-90 text-white rounded-full" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="nav-dashboard-btn">
                Dashboard <ArrowRight className="ml-2" size={14} weight="bold" />
              </Button>
            ) : (
              <>
                <Button onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="hidden md:inline-flex rounded-full px-5 border-2" style={{ borderColor: "var(--tonneva-primary)", color: "var(--tonneva-primary)" }} data-testid="nav-contact-btn">
                  Kontakt
                </Button>
                <Button onClick={goLogin} className="hover:opacity-90 text-white rounded-full px-6" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="nav-login-btn">
                  Anmelden
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 stripes pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.18em] font-bold mb-6"
              style={{ background: "var(--tonneva-accent)", color: "var(--tonneva-primary-dark)" }}>
              <Drop size={12} weight="fill" /> SaaS für Mülltonnenreinigung
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black leading-[1.02] tracking-tight">
              Saubere Tonnen.<br />
              <span style={{ color: "var(--tonneva-primary-dark)" }}>Smart geplant.</span>
            </h1>
            <p className="mt-6 text-lg text-[var(--tonneva-muted)] max-w-2xl">
              Tonneva ist die White-Label-Software für lokale Mülltonnenreinigungs-Unternehmen.
              CRM, Routenplanung, Online-Buchung und Abrechnung — alles in einem System, mit eigenem Corporate Design.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button onClick={goLogin} className="hover:opacity-90 text-white rounded-full h-12 px-8 text-base shadow-lg" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="hero-cta-trial">
                Kostenlos starten <ArrowRight className="ml-2" size={16} weight="bold" />
              </Button>
              <Button variant="outline" className="rounded-full h-12 px-8 text-base bg-white" style={{ borderColor: "var(--tonneva-primary)", color: "var(--tonneva-primary)" }} onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta-how">
                So funktioniert&apos;s
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-[var(--tonneva-muted)]">
              <div className="flex items-center gap-2"><Check size={14} weight="bold" style={{ color: "var(--tonneva-secondary)" }} /> DSGVO-konform</div>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" style={{ color: "var(--tonneva-secondary)" }} /> Hosting in DE</div>
              <div className="flex items-center gap-2"><Check size={14} weight="bold" style={{ color: "var(--tonneva-secondary)" }} /> Eigenes CI</div>
            </div>
          </div>
          <div className="md:col-span-5 relative animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative">
              <div className="relative bg-white rounded-2xl border-2 p-6 shadow-2xl" style={{ borderColor: "var(--tonneva-border)" }}>
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: "var(--tonneva-border)" }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }}><Recycle size={16} weight="bold" className="text-white" /></div>
                    <div className="text-sm font-semibold">Berlin Clean GmbH</div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: "#3FA64E" }}>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#3FA64E" }} /> Live
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Kunden" value="1.284" trend="+12%" />
                  <Stat label="Touren heute" value="14" trend="+3" />
                  <Stat label="Tonnen / Mo" value="9.412" trend="+8%" />
                  <Stat label="MRR" value="€18.4k" trend="+22%" />
                </div>
                <div className="mt-4 p-3 rounded-lg" style={{ background: "var(--tonneva-gradient-soft)" }}>
                  <div className="text-xs">
                    <span className="font-bold text-[var(--tonneva-primary-dark)]">+18 Buchungen</span>{" "}
                    <span className="text-[var(--tonneva-muted)]">in den letzten 24h</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-[var(--tonneva-border)] py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--tonneva-primary)]">02 — Features</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black mt-3 leading-[1.05] tracking-tight">
                Alles, was eine moderne Reinigungsfirma braucht.
              </h2>
            </div>
            <p className="md:col-span-6 md:col-start-7 text-[var(--tonneva-muted)] text-lg">
              Von der ersten Buchung bis zur Rechnungsstellung — Tonneva ersetzt Excel-Listen,
              WhatsApp-Touren und manuelle Abrechnung. Mit eigenem Branding für jeden Mandanten.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Feature icon={<MapPin weight="bold" size={22} />} title="Smarte Routenplanung" body="Tour-Optimierung mit Mapbox. Fahrer sehen ihre tägliche Tour auf dem Smartphone — fertig in 3 Klicks pro Stopp." />
            <Feature icon={<Recycle weight="bold" size={22} />} title="Sync mit Müllabfuhr" body="Automatische Planung gemäß lokalem Abfuhrkalender. Tonnen werden direkt nach der Leerung gereinigt." />
            <Feature icon={<Truck weight="bold" size={22} />} title="Fahrer-App" body="Mobile-first Tour-Ansicht mit Statusupdates, Foto-Upload und Navigationsstart." />
            <Feature icon={<Receipt weight="bold" size={22} />} title="Abrechnung & Buchhaltung" body="Rechnungen mit einem Klick, Export an Lexoffice, sevDesk oder DATEV." />
            <Feature icon={<ChartBar weight="bold" size={22} />} title="CRM & Reporting" body="Privat- und Geschäftskunden, Standorte, Verträge, Tonnen — alles in einer Datenbank." />
            <Feature icon={<Sparkle weight="bold" size={22} />} title="White-Label Portal" body="Eigene Domain, Logo, Farben und Schriften. Endkunden buchen direkt auf Ihrer Marke." />
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="py-24 grain">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--tonneva-primary)]">03 — Workflow</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-3 tracking-tight">In 3 Schritten startklar.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Mandant einrichten", d: "Logo, Farben, Preise und Servicegebiet hochladen — wir generieren Ihr Branded Portal." },
              { n: "02", t: "Kunden onboarden", d: "Endkunden buchen Reinigungen direkt online. Adresse → Tonne → Termin → bezahlt." },
              { n: "03", t: "Tour & abrechnen", d: "Tonneva plant die Tour, Ihr Fahrer fährt, Sie kassieren. Wir kümmern uns um den Rest." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-[var(--tonneva-border)] rounded-2xl p-8 hover:-translate-y-1 transition-transform shadow-sm">
                <div className="font-mono text-sm tracking-wider mb-4" style={{ color: "var(--tonneva-secondary)" }}>{s.n}</div>
                <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">{s.t}</h3>
                <p className="text-sm text-[var(--tonneva-muted)] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24" style={{ background: "linear-gradient(180deg, #0e1a23 0%, #163341 100%)" }}>
        <div className="max-w-7xl mx-auto px-6 text-white">
          <div className="mb-12">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "var(--tonneva-secondary)" }}>04 — Preise</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-3 tracking-tight">Wachsen Sie ohne Risiko.</h2>
            <p className="text-white/70 mt-4 max-w-xl">Monatliche Lizenz pro Mandant. 14 Tage kostenlos testen. Jederzeit kündbar.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { k: "platform_starter", n: "Starter", p: "49", desc: "bis 100 Kunden", feats: ["Branded Portal", "CRM & Touren", "Stripe Checkout"] },
              { k: "platform_pro", n: "Pro", p: "149", desc: "bis 1.000 Kunden", feats: ["Alles aus Starter", "Mapbox Routen", "Buchhaltungs-Export"], featured: true },
              { k: "platform_enterprise", n: "Enterprise", p: "499", desc: "unbegrenzt", feats: ["Alles aus Pro", "Eigene Domain & SSL", "SLA & Support"] },
            ].map((p) => (
              <div key={p.k} className={`p-8 rounded-2xl ${p.featured ? "" : "border border-white/15 bg-white/5"}`}
                style={p.featured ? { background: "var(--tonneva-gradient)" } : {}}>
                <div className={`text-xs uppercase tracking-widest font-bold ${p.featured ? "text-white/90" : "text-white/60"}`}>{p.n}</div>
                <div className="font-heading font-black text-5xl mt-3">€{p.p}<span className="text-base font-medium opacity-70">/Mo</span></div>
                <div className="text-sm mt-1 opacity-70">{p.desc}</div>
                <ul className="mt-6 space-y-2 text-sm">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-center gap-2"><Check size={14} weight="bold" /> {f}</li>
                  ))}
                </ul>
                <Button onClick={goLogin} className={`mt-8 w-full rounded-full ${p.featured ? "bg-white text-[var(--tonneva-primary-dark)] hover:bg-white/95" : "bg-white text-[var(--tonneva-primary-dark)] hover:bg-white/95"}`} data-testid={`pricing-cta-${p.k}`}>
                  Jetzt starten
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FranchiseSection />

      {/* Kontakt */}
      <section id="kontakt" className="py-24 bg-white border-t border-[var(--tonneva-border)]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[var(--tonneva-primary)]">05 — Kontakt</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-3 tracking-tight leading-[1.05]">
              Sprechen Sie mit uns.
            </h2>
            <p className="mt-4 text-lg text-[var(--tonneva-muted)] max-w-md">
              Ob Sie eine Tonnenreinigung buchen möchten oder Ihr Unternehmen auf Tonneva umstellen wollen —
              wir beraten Sie persönlich.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            <a href="tel:+4976215550100" className="p-6 rounded-2xl border border-[var(--tonneva-border)] hover:-translate-y-1 transition bg-white group" data-testid="contact-phone-card">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }}>
                <Phone size={22} weight="bold" />
              </div>
              <div className="text-xs uppercase tracking-widest text-[var(--tonneva-muted)] mb-1">Telefon</div>
              <div className="font-heading text-2xl font-bold tracking-tight group-hover:text-[var(--tonneva-primary)]">+49 7621 555 0100</div>
              <div className="text-sm text-[var(--tonneva-muted)] mt-1">Mo–Fr, 08:00–18:00 Uhr</div>
            </a>
            <a href="mailto:info@tonneva.de" className="p-6 rounded-2xl border border-[var(--tonneva-border)] hover:-translate-y-1 transition bg-white group" data-testid="contact-email-card">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white" style={{ background: "linear-gradient(135deg, #2A86A8 0%, #3FA64E 100%)" }}>
                <EnvelopeSimple size={22} weight="bold" />
              </div>
              <div className="text-xs uppercase tracking-widest text-[var(--tonneva-muted)] mb-1">E-Mail</div>
              <div className="font-heading text-2xl font-bold tracking-tight group-hover:text-[var(--tonneva-primary)]">info@tonneva.de</div>
              <div className="text-sm text-[var(--tonneva-muted)] mt-1">Antwort innerhalb 24h</div>
            </a>
            <div className="p-6 rounded-2xl border border-[var(--tonneva-border)] bg-white sm:col-span-2" data-testid="contact-address-card">
              <div className="text-xs uppercase tracking-widest text-[var(--tonneva-muted)] mb-2">Adresse</div>
              <div className="font-heading text-lg font-semibold tracking-tight">
                Tonneva GmbH · Kanderner Str. 12 · 79576 Weil am Rhein · Deutschland
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href="tel:+4976215550100" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold hover:opacity-90" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="contact-call-cta">
                  <Phone size={14} weight="bold" /> Jetzt anrufen
                </a>
                <a href="#franchise" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-sm font-semibold hover:bg-zinc-50" style={{ borderColor: "var(--tonneva-primary)", color: "var(--tonneva-primary)" }} data-testid="contact-franchise-cta">
                  Franchise-Anfrage
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--tonneva-border)] py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[var(--tonneva-muted)]">
          <div className="flex items-center gap-3">
            <Logo size={24} textClass="text-sm" />
            <span>© {new Date().getFullYear()} — Made in Germany</span>
          </div>
          <div className="flex flex-wrap gap-6 items-center">
            <a href="tel:+4976215550100" className="hover:text-[var(--tonneva-primary)] flex items-center gap-1" data-testid="footer-phone"><Phone size={12} weight="bold" /> +49 7621 555 0100</a>
            <a href="mailto:info@tonneva.de" className="hover:text-[var(--tonneva-primary)]" data-testid="footer-email">info@tonneva.de</a>
            <a href="/portal/berlin-clean" className="hover:text-[var(--tonneva-primary)]" data-testid="footer-demo-portal">Demo-Portal</a>
            <a href="/datenschutz" className="hover:text-[var(--tonneva-primary)]" data-testid="footer-datenschutz">Datenschutz</a>
            <a href="/agb" className="hover:text-[var(--tonneva-primary)]" data-testid="footer-agb">AGB</a>
            <a href="/impressum" className="hover:text-[var(--tonneva-primary)]" data-testid="footer-impressum">Impressum</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ label, value, trend }) => (
  <div className="rounded-lg p-3 bg-[var(--tonneva-accent)]">
    <div className="text-[10px] uppercase tracking-widest text-[var(--tonneva-muted)]">{label}</div>
    <div className="font-mono text-xl font-medium mt-1 text-[var(--tonneva-fg)]">{value}</div>
    <div className="text-[10px] mt-1 font-bold" style={{ color: "var(--tonneva-secondary)" }}>{trend}</div>
  </div>
);

const Feature = ({ icon, title, body }) => (
  <div className="p-6 border border-[var(--tonneva-border)] hover:-translate-y-1 transition-transform bg-white rounded-2xl shadow-sm hover:shadow-md">
    <div className="w-12 h-12 rounded-xl bg-tonneva-gradient text-white flex items-center justify-center mb-4">{icon}</div>
    <h3 className="font-heading text-xl font-bold tracking-tight mb-2">{title}</h3>
    <p className="text-sm text-[var(--tonneva-muted)] leading-relaxed">{body}</p>
  </div>
);
